import { useEffect } from 'react';
import { Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from './lib/auth.jsx';
import { initAnalytics, trackPageView } from './lib/analytics.js';
import Login from './pages/Login.jsx';
import Signup from './pages/Signup.jsx';
import ResetPassword from './pages/ResetPassword.jsx';
import AdminDashboard from './pages/AdminDashboard.jsx';
import AcceptInvite from './pages/AcceptInvite.jsx';
import RequesterApp from './pages/RequesterApp.jsx';
import ResponderApp from './pages/ResponderApp.jsx';
import LandingPage from './pages/LandingPage.jsx';
import Pricing from './pages/Pricing.jsx';
import SuperBilling from './pages/SuperBilling.jsx';
import QuickReport from './pages/QuickReport.jsx';

// Top-level router. Public routes (login/signup/reset) render whatever the URL says.
// Protected routes redirect to /login if not authenticated, then route by role.

function ProtectedRoute({ allow, children }) {
  const { user, claims, loading } = useAuth();
  if (loading) return <div className="centered">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (allow && !allow.includes(claims?.role)) {
    return <div className="centered">Your account doesn't have access to this page.</div>;
  }
  return children;
}

function RoleHome() {
  const { user, claims, loading } = useAuth();
  if (loading) return <div className="centered">Loading…</div>;
  // Not signed in → show the public landing page (marketing site)
  if (!user) return <LandingPage />;
  // Signed in → route by role
  switch (claims?.role) {
    case 'vendor_admin':
    case 'super_admin':
      return <Navigate to="/admin" replace />;
    case 'responder':
      return <Navigate to="/responder" replace />;
    case 'customer':
      return <Navigate to="/app" replace />;
    default:
      return <Navigate to="/login" replace />;
  }
}

// Fires Google Analytics page views on every client-side route change. Renders
// nothing. No-op until VITE_GA_MEASUREMENT_ID is set (see lib/analytics.js).
function RouteAnalytics() {
  const location = useLocation();
  useEffect(() => { initAnalytics(); }, []);
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return null;
}

export default function App() {
  return (
    <>
      <RouteAnalytics />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/reset" element={<ResetPassword />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/invite/:vendorId/:inviteId" element={<AcceptInvite />} />
        {/* QR instant access — a printed sticker lands here. Public by design:
            the page signs the visitor in anonymously and claims the access
            point with the token from the URL. */}
        <Route path="/r/:vendorId/:accessPointId" element={<QuickReport />} />

        <Route
          path="/admin/*"
          element={
            <ProtectedRoute allow={['vendor_admin', 'super_admin']}>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/responder/*"
          element={
            <ProtectedRoute allow={['responder', 'vendor_admin']}>
              <ResponderApp />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app"
          element={
            <ProtectedRoute allow={['customer']}>
              <RequesterApp />
            </ProtectedRoute>
          }
        />
        <Route
          path="/super-billing"
          element={
            <ProtectedRoute allow={['super_admin']}>
              <SuperBilling />
            </ProtectedRoute>
          }
        />

        <Route path="/" element={<RoleHome />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
}

// 404 — replaces the previous silent Navigate("/") catch-all (verification audit B7).
// Keeps the typed URL visible so the user understands what happened, and offers a way home.
function NotFound() {
  return (
    <div className="centered" style={{ flexDirection: 'column', gap: 16, padding: 24 }}>
      <div style={{ fontSize: 64, lineHeight: 1, opacity: 0.4 }}>404</div>
      <div style={{ fontSize: 18, fontWeight: 600 }}>Page not found</div>
      <div style={{ color: 'var(--muted)', maxWidth: 420, textAlign: 'center', fontSize: 14 }}>
        The page you're looking for doesn't exist — likely a typo or a stale link.
      </div>
      <Link to="/" className="btn-primary" style={{ textDecoration: 'none' }}>
        ← Back to home
      </Link>
    </div>
  );
}
